import { useState } from 'react';
import type { ExpenseDTO, WithdrawalSource } from '@six-pockets/shared';
import { SegmentedControl } from '@/components/ui';
import { ManageFormModal } from '@/features/manage/components/ManageFormModal';
import { formatINR } from '@/utils/format';
import { SOURCE_LABEL, WITHDRAWAL_LABEL, WITHDRAWAL_OPTIONS } from '../sources';
import styles from '@/styles/components/ConfirmModal/style.scss';

interface WithdrawModalProps {
  expense: ExpenseDTO;
  busy: boolean;
  error: string | null;
  onWithdraw: (from: WithdrawalSource) => void;
  onClose: () => void;
}

/**
 * Withdraw: whoever paid an expense takes the whole amount back, from the cash counter (it then shows in the cash
 * book's passbook) or the bank. The button names the source, so it isn't picked by accident. Mount it only while open.
 */
export function WithdrawModal({ expense, busy, error, onWithdraw, onClose }: WithdrawModalProps) {
  const [from, setFrom] = useState<WithdrawalSource>('counter');

  return (
    <ManageFormModal
      title={`Withdraw ${formatINR(expense.amount)}`}
      submitLabel={`Withdraw from ${WITHDRAWAL_LABEL[from].toLowerCase()}`}
      busy={busy}
      error={error}
      onSubmit={() => onWithdraw(from)}
      onClose={onClose}
    >
      <p className={styles.message}>
        <strong>{SOURCE_LABEL[expense.source]}</strong> takes <strong>{formatINR(expense.amount)}</strong> back from the business for{' '}
        <strong>{expense.reason}</strong>. A withdrawn expense can no longer be edited or deleted.
      </p>
      <SegmentedControl legend="Taken from" options={WITHDRAWAL_OPTIONS} value={from} onChange={setFrom} />
    </ManageFormModal>
  );
}
